import { Link } from 'react-router-dom'
import DocLayout from '../../components/docs/DocLayout'
import { contrastRatio } from '@/content/color'
import { america, findMission, themeFor } from '@/content/missions'
import { contrastPairs, roleDescriptions, roleNames, type RoleName } from '@/content/theme'
import { usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'
import MissionSwitcher from '@/components/missions/MissionSwitcher'

const groups: Array<{ title: string; roles: RoleName[] }> = [
  { title: 'Surfaces', roles: ['bg', 'surface', 'raised', 'border', 'border-strong'] },
  { title: 'Text', roles: ['ink', 'ink-muted', 'link', 'accent-ink'] },
  { title: 'Actions and accents', roles: ['primary', 'primary-hover', 'on-primary', 'accent', 'on-accent', 'focus'] },
  { title: 'Status', roles: ['success', 'warning', 'error', 'info'] },
  { title: 'Flag', roles: ['flag-red', 'flag-blue'] },
]

function Swatch({ value, label }: { value: string; label: string }) {
  return (
    <span className="fnd-role__swatch">
      <span className="fnd-role__chip" style={{ background: value }} aria-hidden="true" />
      <span className="mds-visually-hidden">{label} </span>
      <code>{value}</code>
    </span>
  )
}

export default function ColorPage() {
  const { prefs } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  const theme = themeFor(mission)

  return (
    <DocLayout section={{ label: 'Foundations', to: '/foundations' }}
      eyebrow="Foundations"
      title="Color"
      intro={
        <p>
          Red, white and blue by default, any agency on request. Components read semantic roles, so one attribute on the
          root element changes the mode or the mission across every screen, and every pairing a component makes is
          checked for contrast.
        </p>
      }
      wide
    >
      <section className="doc-section" aria-labelledby="color-mission">
        <h2 id="color-mission"><Cased>The active theme</Cased></h2>
        <p>
          The values below belong to <strong>{mission.name}</strong>. Choose another mission to see its roles, here or
          with the mission button at the top of any page; the whole site follows.
        </p>
        <MissionSwitcher />
        <div className="fnd-modes">
          {(['light', 'dark'] as const).map((mode) => (
            <div key={mode} className="fnd-mode" data-theme={mode} data-mission={mission.id}>
              <span className="mds-eyebrow">{mode}</span>
              <p className="fnd-mode__title">{mission.kind === 'nation' ? 'Hello, America' : mission.name}</p>
              <p className="fnd-mode__body">
                Body text in ink, <span className="fnd-mode__muted">secondary text in ink-muted</span>, and{' '}
                <span className="fnd-mode__link">a link</span>.
              </p>
              <div className="mds-cluster">
                <span className="mds-button mds-button--primary mds-button--sm">Primary</span>
                <span className="mds-button mds-button--secondary mds-button--sm">Secondary</span>
                <span className="mds-tag mds-tag--accent">Accent</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="color-roles">
        <h2 id="color-roles"><Cased>Roles</Cased></h2>
        <p>
          Each role is a CSS custom property named <code>--mds-&lt;role&gt;</code>. Use the role that describes the job,
          never a raw value, and the theme takes care of light, dark and mission.
        </p>
        {groups.map((group) => (
          <div key={group.title} className="fnd-role-group">
            <h3><Cased>{group.title}</Cased></h3>
            <div className="doc-table-wrap" tabIndex={0}>
              <table className="doc-table fnd-role-table">
                <thead>
                  <tr>
                    <th scope="col">Role</th>
                    <th scope="col">Light</th>
                    <th scope="col">Dark</th>
                    <th scope="col">Use</th>
                  </tr>
                </thead>
                <tbody>
                  {group.roles.map((role) => (
                    <tr key={role}>
                      <th scope="row">
                        <code>--mds-{role}</code>
                      </th>
                      <td>
                        <Swatch value={theme.light[role]} label="Light" />
                      </td>
                      <td>
                        <Swatch value={theme.dark[role]} label="Dark" />
                      </td>
                      <td className="mds-muted">{roleDescriptions[role]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        <p className="mds-muted">
          {roleNames.length} roles in all. The full set for every mission is in{' '}
          <a href="/mds/tokens.json">tokens.json</a> and <a href="/mds/themes/index.json">the theme index</a>.
        </p>
      </section>

      <section className="doc-section" aria-labelledby="color-contrast">
        <h2 id="color-contrast"><Cased>Contrast</Cased></h2>
        <p>
          These are the only pairings components make. Text pairs meet 4.5:1 and control boundaries and focus rings meet
          3:1, the WCAG 2.1 AA thresholds. Ink is held to 7:1. The generator enforces every pair for every mission in both modes.
        </p>
        <div className="doc-table-wrap" tabIndex={0}>
          <table className="doc-table fnd-contrast">
            <thead>
              <tr>
                <th scope="col">Foreground</th>
                <th scope="col">Background</th>
                <th scope="col">Minimum</th>
                <th scope="col">Light</th>
                <th scope="col">Dark</th>
              </tr>
            </thead>
            <tbody>
              {contrastPairs.map(([fg, bg, min]) => (
                <tr key={`${fg}-${bg}`}>
                  <th scope="row">
                    <code>{fg}</code>
                  </th>
                  <td>
                    <code>{bg}</code>
                  </td>
                  <td>{min}:1</td>
                  {(['light', 'dark'] as const).map((mode) => {
                    const ratio = contrastRatio(theme[mode][fg], theme[mode][bg])
                    return (
                      <td key={mode}>
                        <span className="fnd-contrast__cell">
                          {min < 4.5 ? (
                            <span
                              className="fnd-contrast__sample fnd-contrast__sample--line"
                              style={{ background: theme[mode][bg], boxShadow: `inset 0 0 0 2px ${theme[mode][fg]}` }}
                              aria-hidden="true"
                            />
                          ) : (
                            <span
                              className="fnd-contrast__sample"
                              style={{ color: theme[mode][fg], background: theme[mode][bg] }}
                              aria-hidden="true"
                            >
                              Aa
                            </span>
                          )}
                          {ratio}:1
                        </span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="color-america">
        <h2 id="color-america"><Cased>The American palette</Cased></h2>
        <p>
          The default theme is a deep navy for actions, a clear federal blue for links, a true red for accents and an ink
          with a trace of blue. Dark mode keeps the same roles, with a near-black background and a brighter blue so
          actions keep their weight.
        </p>
        <div className="fnd-palette">
          {(['primary', 'accent', 'link', 'ink'] as const).map((role) => (
            <div key={role} className="fnd-palette__item">
              <span className="fnd-palette__chip" style={{ background: themeFor(america).light[role] }} />
              <span className="fnd-palette__chip fnd-palette__chip--dark" style={{ background: themeFor(america).dark[role] }} />
              <span className="fnd-palette__name">{role}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="color-missions">
        <h2 id="color-missions"><Cased>Mission themes</Cased></h2>
        <p>
          Each agency theme starts from three colors in its own brand guide, website or seal: a primary, a secondary and
          an accent. The generator keeps each hue and moves lightness until every role pair passes, then publishes the
          result as CSS and JSON. Nothing is generated at runtime.
        </p>
        <ol>
          <li>
            Put <code>data-mission="usda"</code> on the root element to reskin a whole site, or on any element to preview a
            theme in place.
          </li>
          <li>
            Add <code>data-theme="dark"</code> on the same element to pin its mode.
          </li>
          <li>
            Or download a single theme, such as <a href="/mds/themes/usda.css">usda.css</a>, which sets the roles on{' '}
            <code>:root</code> with a dark mode included.
          </li>
        </ol>
        <p>
          <Link to="/missions" className="mds-link">
            Browse the mission themes
          </Link>
        </p>
      </section>

      <section className="doc-section" aria-labelledby="color-usage">
        <h2 id="color-usage"><Cased>Usage</Cased></h2>
        <div className="fnd-dodont">
          <div className="fnd-dodont__item fnd-dodont__item--do">
            <h3><Cased>Do</Cased></h3>
            <ul>
              <li>Use primary for the one action that moves a task forward.</li>
              <li>Use accent sparingly: badges, highlights, the flag stripe.</li>
              <li>Pair color with a word or an icon for every status.</li>
              <li>Use accent-ink when the accent color carries text.</li>
            </ul>
          </div>
          <div className="fnd-dodont__item fnd-dodont__item--dont">
            <h3><Cased>Don't</Cased></h3>
            <ul>
              <li>Write a hex value into a component.</li>
              <li>Set text on accent unless it uses on-accent.</li>
              <li>Use border for an input outline; controls take border-strong.</li>
              <li>Recolor the official flag; it keeps its own colors in every theme.</li>
            </ul>
          </div>
        </div>
      </section>
    </DocLayout>
  )
}
